import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { environment } from 'src/environments/environment';

export interface DocumentRequirement {
  id: number;
  document_code: string;
  document_name: string;
  description: string;
  issuing_authority: string;
  stage: 'feasibility' | 'sanction' | 'completion';
  domain: string;
  sub_domain: string;
  is_mandatory: boolean;
  document_category: string;
  sequence_order: number;
  notes: string;
  // Tracking fields
  tracking_id?: number;
  status?: string;
  applied_date?: string;
  submission_date?: string;
  approval_date?: string;
  expiry_date?: string;
  reference_number?: string;
  remarks?: string;
  rejection_reason?: string;
  assigned_to?: number;
  assigned_to_name?: string;
  priority?: string;
  document_id?: number;
  file_name?: string;
  file_path?: string;
}

export interface DocumentStats {
  total: number;
  mandatory: number;
  approved: number;
  pending: number;
  in_progress: number;
  rejected: number;
  completion_percentage: number;
}

@Component({
  selector: 'app-construction-document-checklist',
  templateUrl: './construction-document-checklist.component.html',
  styleUrls: ['./construction-document-checklist.component.css']
})
export class ConstructionDocumentChecklistComponent implements OnInit, OnChanges {
  @Input() projectId!: number;
  @Input() domain: string = 'Construction';
  @Input() subDomain: string = 'Residential';
  @Output() documentUpdated = new EventEmitter<any>();

  isLoading = false;
  project: any = null;
  documents: DocumentRequirement[] = [];
  uploadedDocuments: any[] = [];
  
  groupedDocuments: {
    feasibility: DocumentRequirement[];
    sanction: DocumentRequirement[];
    completion: DocumentRequirement[];
  } = {
    feasibility: [],
    sanction: [],
    completion: []
  };

  stats: DocumentStats = {
    total: 0,
    mandatory: 0,
    approved: 0,
    pending: 0,
    in_progress: 0,
    rejected: 0,
    completion_percentage: 0
  };

  selectedDocument: DocumentRequirement | null = null;
  showUpdateDialog = false;
  updateForm: FormGroup;
  employees: any[] = [];

  statusOptions = [
    { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-800' },
    { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-800' },
    { value: 'submitted', label: 'Submitted', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-800' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
    { value: 'expired', label: 'Expired', color: 'bg-orange-100 text-orange-800' },
    { value: 'not_applicable', label: 'Not Applicable', color: 'bg-purple-100 text-purple-800' }
  ];

  priorityOptions = [
    { value: 'low', label: 'Low', color: 'text-gray-500' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
    { value: 'high', label: 'High', color: 'text-orange-500' },
    { value: 'critical', label: 'Critical', color: 'text-red-500' }
  ];

  expandedStages: { [key: string]: boolean } = {
    feasibility: true,
    sanction: false,
    completion: false
  };

  filterStatus: string = '';
  filterMandatory: string = '';
  searchKeyword: string = '';

  constructor(
    private apiService: ApiService,
    private toastr: ToastrMsgService,
    private fb: FormBuilder
  ) {
    this.updateForm = this.fb.group({
      status: ['pending'],
      applied_date: [null],
      submission_date: [null],
      approval_date: [null],
      expiry_date: [null],
      reference_number: [''],
      remarks: [''],
      assigned_to: [null],
      priority: ['medium']
    });
  }

  ngOnInit(): void {
    this.tryLoad();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && !changes['projectId'].firstChange) {
      this.tryLoad();
    }
  }

  private tryLoad(): void {
    if (this.projectId) {
      this.loadProjectDocuments();
      this.loadEmployees();
    }
  }

  loadProjectDocuments(): void {
    this.isLoading = true;
    this.apiService.getProjectConstructionDocs(this.projectId).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.project = res.project;
          this.documents = res.data || [];
          this.uploadedDocuments = res.uploaded_documents || [];
          this.groupedDocuments = res.grouped || {
            feasibility: [],
            sanction: [],
            completion: []
          };
          this.stats = res.stats || this.stats;

          if (this.documents.length === 0) {
            this.initializeDocuments();
          }
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading project documents:', err);
        this.toastr.error('Failed to load document requirements');
        this.isLoading = false;
      }
    });
  }

  initializeDocuments(): void {
    this.apiService.initializeProjectDocs(this.projectId).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.loadProjectDocuments();
        }
      },
      error: (err) => {
        console.error('Error initializing documents:', err);
      }
    });
  }

  loadEmployees(): void {
    this.apiService.getStaff({ keyword: '' }).subscribe({
      next: (res: any) => {
        this.employees = res.data || res || [];
      },
      error: (err) => console.error('Error loading employees:', err)
    });
  }

  toggleStage(stage: string): void {
    this.expandedStages[stage] = !this.expandedStages[stage];
  }

  getStatusClass(status: string): string {
    const option = this.statusOptions.find(s => s.value === status);
    return option ? option.color : 'bg-gray-100 text-gray-800';
  }

  getStatusLabel(status: string): string {
    const option = this.statusOptions.find(s => s.value === status);
    return option ? option.label : 'Pending';
  }

  getPriorityClass(priority: string): string {
    const option = this.priorityOptions.find(p => p.value === priority);
    return option ? option.color : 'text-gray-500';
  }

  getStageIcon(stage: string): string {
    switch (stage) {
      case 'feasibility': return 'search';
      case 'sanction': return 'gavel';
      case 'completion': return 'check_circle';
      default: return 'description';
    }
  }

  getStageLabel(stage: string): string {
    switch (stage) {
      case 'feasibility': return 'Feasibility Stage';
      case 'sanction': return 'Sanction Stage';
      case 'completion': return 'After Completion';
      default: return stage;
    }
  }

  getStageCount(stage: string): number {
    return this.groupedDocuments[stage as keyof typeof this.groupedDocuments]?.length || 0;
  }

  getStageApprovedCount(stage: string): number {
    const docs = this.groupedDocuments[stage as keyof typeof this.groupedDocuments] || [];
    return docs.filter(d => d.status === 'approved').length;
  }

  openUpdateDialog(doc: DocumentRequirement): void {
    this.selectedDocument = doc;
    this.updateForm.patchValue({
      status: doc.status || 'pending',
      applied_date: doc.applied_date,
      submission_date: doc.submission_date,
      approval_date: doc.approval_date,
      expiry_date: doc.expiry_date,
      reference_number: doc.reference_number || '',
      remarks: doc.remarks || '',
      assigned_to: doc.assigned_to,
      priority: doc.priority || 'medium'
    });
    this.showUpdateDialog = true;
  }

  closeUpdateDialog(): void {
    this.showUpdateDialog = false;
    this.selectedDocument = null;
    this.updateForm.reset({
      status: 'pending',
      priority: 'medium'
    });
  }

  saveDocumentUpdate(): void {
    if (!this.selectedDocument) return;

    const formData = this.updateForm.value;
    
    this.apiService.updateProjectDocTracking(
      this.projectId,
      this.selectedDocument.id,
      formData
    ).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastr.success('Document updated successfully');
          this.closeUpdateDialog();
          this.loadProjectDocuments();
          this.documentUpdated.emit(res.data);
        }
      },
      error: (err) => {
        console.error('Error updating document:', err);
        this.toastr.error('Failed to update document');
      }
    });
  }

  quickStatusUpdate(doc: DocumentRequirement, status: string): void {
    this.apiService.updateProjectDocTracking(
      this.projectId,
      doc.id,
      { status }
    ).subscribe({
      next: (res: any) => {
        if (res.success) {
          doc.status = status;
          this.toastr.success('Status updated');
          this.loadProjectDocuments();
        }
      },
      error: (err) => {
        console.error('Error updating status:', err);
        this.toastr.error('Failed to update status');
      }
    });
  }

  getFilteredDocuments(stage: string): DocumentRequirement[] {
    let docs = this.groupedDocuments[stage as keyof typeof this.groupedDocuments] || [];
    
    if (this.filterStatus) {
      docs = docs.filter(d => d.status === this.filterStatus || (!d.status && this.filterStatus === 'pending'));
    }

    if (this.filterMandatory === 'true') {
      docs = docs.filter(d => d.is_mandatory);
    } else if (this.filterMandatory === 'false') {
      docs = docs.filter(d => !d.is_mandatory);
    }

    if (this.searchKeyword) {
      const keyword = this.searchKeyword.toLowerCase();
      docs = docs.filter(d => 
        d.document_name.toLowerCase().includes(keyword) ||
        d.issuing_authority?.toLowerCase().includes(keyword) ||
        d.document_code?.toLowerCase().includes(keyword)
      );
    }

    return docs;
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterMandatory = '';
    this.searchKeyword = '';
  }

  getFileUrl(doc: DocumentRequirement): string | null {
    if (!doc.file_path && !doc.file_name) return null;
    const path = doc.file_path || doc.file_name || '';
    if (/^https?:\/\//i.test(path)) return path;
    const base = environment.imageBaseUrl || '';
    const cleaned = path.replace(/^\/+/, '').replace(/^uploads\//, '');
    return `${base}${cleaned}`;
  }

  exportChecklist(): void {
    const headers = ['Stage', 'Document Code', 'Document Name', 'Authority', 'Mandatory', 'Status', 'Reference No', 'Applied Date', 'Approval Date', 'Expiry Date', 'Remarks'];
    const rows = this.documents.map(doc => [
      this.getStageLabel(doc.stage),
      doc.document_code,
      doc.document_name,
      doc.issuing_authority || '',
      doc.is_mandatory ? 'Yes' : 'No',
      this.getStatusLabel(doc.status || 'pending'),
      doc.reference_number || '',
      doc.applied_date || '',
      doc.approval_date || '',
      doc.expiry_date || '',
      doc.remarks || ''
    ]);

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `document_checklist_${this.projectId}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
